import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Chip } from 'shared/ui/Chip';
import { useToastCtx } from 'shared/state/configContexts';

/** Tokenised replacement for ToastContainer's inline-styled stack. Same shape
 *  ({ badge, title, message }) and the same 30 s default from useToast, so
 *  every existing showToast call site works untouched. Deliberately omits the
 *  classic container's hard-coded "Generating files automatically…" status
 *  line under badged toasts — that copy is only true for the CCI path and
 *  doesn't belong baked into a generic container (ticket recorded to make it
 *  a per-toast field if product wants it back). */
export function Toasts() {
  const { toasts, dismissToast } = useToastCtx();
  if (!toasts || toasts.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed right-5 top-5 z-[100] flex w-80 flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          data-testid="toast"
          className="pointer-events-auto rounded-[var(--radius-2)] border border-hairline/25 bg-surface-overlay p-3 shadow-xl"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {t.badge && <Chip tone="brand" className="mb-1">{t.badge}</Chip>}
              <p className="font-display text-sm font-semibold text-ink">{t.title}</p>
              <p className="mt-0.5 font-body text-xs text-ink-muted">{t.message}</p>
            </div>
            <button
              type="button"
              aria-label={`Dismiss ${t.title}: ${t.message}`}
              onClick={() => dismissToast(t.id)}
              className="rounded-[var(--radius-2)] p-1 text-ink-muted hover:bg-surface-high hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
